import React, { useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ViewStyle,
} from 'react-native';
import DatePicker from 'react-native-date-picker';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';

interface DatePickerInputProps {
    label: string;
    value: string;
    onChangeDate: (date: string) => void;
    error?: string;
    containerStyle?: ViewStyle;
    placeholder?: string;
    mode?: 'date' | 'time' | 'datetime';
    maximumDate?: Date;
    minimumDate?: Date;
}

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
    label,
    value,
    onChangeDate,
    error,
    containerStyle,
    placeholder = 'Select date',
    mode = 'date',
    maximumDate,
    minimumDate,
}) => {
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(() => {
        if (value) {
            const date = new Date(value);
            return isNaN(date.getTime()) ? new Date() : date;
        }
        return new Date();
    });

    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Format date for display (DD/MM/YYYY)
    const displayDate = (dateStr: string): string => {
        if (!dateStr) return '';

        // Handle ISO string or YYYY-MM-DD
        let cleanDate = dateStr;
        if (dateStr.includes('T')) {
            cleanDate = dateStr.split('T')[0];
        }

        const parts = cleanDate.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
    };

    const handleConfirm = (date: Date) => {
        setSelectedDate(date);
        onChangeDate(formatDate(date));
        setOpen(false);
    };

    const handleCancel = () => {
        setOpen(false);
    };

    return (
        <View style={[styles.container, containerStyle]}>
            <Text style={styles.label}>{label}</Text>
            <TouchableOpacity
                style={[styles.inputContainer, error && styles.inputContainer_error]}
                onPress={() => setOpen(true)}
                activeOpacity={0.7}>
                <Icon
                    name="calendar"
                    size={18}
                    color={theme.colors.textSecondary}
                    style={styles.icon}
                />
                <Text
                    style={[
                        styles.inputText,
                        !value && styles.inputText_placeholder,
                    ]}
                    numberOfLines={1}>
                    {value ? displayDate(value) : placeholder}
                </Text>
                <Icon
                    name="chevron-down"
                    size={18}
                    color={theme.colors.textSecondary}
                />
            </TouchableOpacity>
            {error && <Text style={styles.error}>{error}</Text>}

            <DatePicker
                modal
                mode={mode}
                open={open}
                date={selectedDate}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                theme="light"
                maximumDate={maximumDate}
                minimumDate={minimumDate}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        marginBottom: theme.spacing.md,
    },
    label: {
        ...theme.typography.body2,
        color: theme.colors.text,
        marginBottom: theme.spacing.sm,
        fontWeight: '600',
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        borderWidth: 1,
        borderColor: theme.colors.border,
        paddingHorizontal: theme.spacing.sm, // Reduced from md
        paddingVertical: theme.spacing.md, // Reduced from md
    },
    inputContainer_error: {
        borderColor: theme.colors.error,
    },
    icon: {
        marginRight: theme.spacing.xs, // Reduced from sm
    },
    inputText: {
        flex: 1,
        ...theme.typography.body2, // Changed from body1 to body2 for smaller font
        color: theme.colors.text,
    },
    inputText_placeholder: {
        color: theme.colors.textLight,
    },
    error: {
        ...theme.typography.caption,
        color: theme.colors.error,
        marginTop: theme.spacing.xs,
    },
});
